import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Stack, Switch, Typography } from '@esgian/esgianui';
import { API_ROLLUP, DATE_FORMAT, DISPLAY_UNIT, TIMESTAMP, WARNINGS_TYPE } from '@constants/index';
import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import { fetchAnalyticsData, fetchOverviewKeyFiguresDataV2 } from '@api/Rig';
import { findOverviewSignal } from '@api/helpers';

import { RigSelect, TextInput } from '@components/Inputs';
import DateRangeDropdown from '@components/DateRangeDropdown';
import TargetDetailsInput from '@components/Inputs/BaselineInput/BaselineModal/TargetDetailsInput';
import TargetDetailsAnalyticsInput from '@components/Inputs/BaselineInput/BaselineModal/TargetDetailsAnalyticsInput';
import { parseNumberToSignificant, roundToSixDecimals } from '@helpers/numberFormatter';
import { formatNumberReadable, getTotSum } from '@helpers/helpers';
import { BaselineTargetOverviewFields, BaselineType } from '@typesFiles/baseline';
import { SelectedRigType } from '@typesFiles/redux';
import tzLookup from 'tz-lookup';
import { AnalyticsV2RespType, overviewKeyFiguresV2RespType } from '@typesFiles/apis';
import { TextWithTooltipIcon } from '@components/Display';
import { Collapse } from '@mui/material';
import PercentInput from '@components/Inputs/PercentInput';

const overviewFields: {
  fieldName: keyof BaselineTargetOverviewFields;
  unit: string;
  title: string;
}[] = [
  { fieldName: 'energyConsumption', unit: 'MWh', title: 'Energy consumption' },
  { fieldName: 'fuelConsumption', unit: 'mt', title: 'Fuel consumption' },
  { fieldName: 'co2Emissions', unit: 'mt', title: 'CO2 emissions' },
  { fieldName: 'noxEmissions', unit: 'kg', title: 'NOx emissions' }
];

const analyticsFields = {
  [WARNINGS_TYPE.TooManyEng]: { title: 'Too many engines running', fieldName: 'engines' },
  [WARNINGS_TYPE.MP_AUX_Idle]: { title: 'Idle mud pumps', fieldName: 'mudpumps' },
  [WARNINGS_TYPE.TooManyHPU]: { title: 'Too many HPU pumps online', fieldName: 'hpu' },
  [WARNINGS_TYPE.DW_AUX_Idle]: { title: 'Idle drawworks', fieldName: 'dw' },
  [WARNINGS_TYPE.TooManyAgitators]: { title: 'Idle agitators', fieldName: 'agitators' },
};

const getDailyAvg = (value: number | null | undefined, days: number) => {
  if (!value) {
    return null;
  }
  return value / days;
  // return roundToSixDecimals(value / days);
};

const updateBaselineDailyValue = (
  value: number | null,
  duration: number,
  daily: boolean
): number | null => {
  if (value === null) return null;
  if (daily) {
    return value / duration;
  }
  return value * duration;
};

const getApiStartAndEndDate = (
  selectedRig: SelectedRigType,
  startDate: string,
  endDate: string
) => {
  const timeZone = tzLookup(selectedRig.latitude, selectedRig.longitude);
  let start = moment(startDate).startOf('day').format(TIMESTAMP);
  const end = moment(endDate).endOf('day').format(TIMESTAMP);
  start = `${start}${moment(startDate).tz(timeZone).format('Z')}`;
  return { start, end };
};

type Props = {
  highlightErrors: boolean;
  baseline: BaselineType;
  setBaseline: Dispatch<SetStateAction<BaselineType>>;
};

function TargetDetails({ highlightErrors, baseline, setBaseline }: Props) {
  const [percentChange, setPercentChange] = useState<number>(0);

  const periodDetails = useMemo(() => {
    if (!baseline.startDate || !baseline.endDate) {
      return { startDate: null, endDate: null, duration: 1 };
    }
    const { startDate, endDate } = baseline;
    let momentEnd = moment.utc(endDate, DATE_FORMAT).endOf('day');
    if (momentEnd.isAfter(moment.utc())) {
      momentEnd = moment.utc().endOf('day');
    }
    const duration = Math.ceil(
      moment.duration(momentEnd.diff(moment.utc(startDate, DATE_FORMAT))).asDays()
    );
    return { startDate, endDate, duration };
  }, [baseline.startDate, baseline.endDate]);

  const defaultOverviewDataQuery = useQuery({
    queryKey: [
      'createBaseline',
      'overview',
      {
        selectedRig: baseline.selectedRig,

        periodDetails
      }
    ],
    enabled: !!baseline.selectedRig && !!baseline.startDate && !!baseline.endDate,
    placeholderData: null,
    queryFn: ({ signal }) => {
      const { selectedRig, startDate, endDate } = baseline;
      if (!selectedRig || !startDate || !endDate) return null;
      const { start, end } = getApiStartAndEndDate(selectedRig, startDate, endDate);
      return fetchOverviewKeyFiguresDataV2(
        signal,
        { selectedRig: selectedRig, startDate: start, endDate: end },
        API_ROLLUP.HOUR
      ).then((resp: any) => {
        if (!resp?.calculations || !Array.isArray(resp.calculations) || resp.calculations.length === 0) {
          return null;
        }
        
        const rigData = resp.calculations.find((rig: any) => rig.id == selectedRig.rigId);
        if (!rigData) return null;
        
        const signals = rigData.signals || [];
        const fuelSignal = findOverviewSignal(signals, 'Rig', 'Fuel');
        const co2Signal = findOverviewSignal(signals, 'Rig', 'CO2');
        const energySignal = findOverviewSignal(signals, 'Rig', 'Energy');
        const noxSignal = findOverviewSignal(signals, 'Rig', 'NOx');

        const totals: { [key: string]: number } = {
          fuelConsumption: getTotSum(fuelSignal?.timeSeriesValues || {}),
          co2Emissions: getTotSum(co2Signal?.timeSeriesValues || {}),
          noxEmissions: getTotSum(noxSignal?.timeSeriesValues || {}),
          energyConsumption: getTotSum(energySignal?.timeSeriesValues || {}) / 1000
        };
        const { duration } = periodDetails;
        const daily: { [key: string]: number | null } = {};
        overviewFields.forEach(({ fieldName }) => {
          daily[fieldName] = getDailyAvg(totals[fieldName], duration);
        });

        return {
          total: totals,
          daily: daily
        };
      });
    }
  });

  const defaultAnalyticsDataQuery = useQuery({
    queryKey: [
      'createBaseline',
      'analytics',
      {
        selectedRig: baseline.selectedRig,
        periodDetails: periodDetails
      }
    ],
    enabled: !!baseline.selectedRig && !!baseline.startDate && !!baseline.endDate,
    placeholderData: null,
    queryFn: ({ signal }) => {
      const { selectedRig, startDate, endDate } = baseline;
      if (!selectedRig || !startDate || !endDate) return null;

      const { start, end } = getApiStartAndEndDate(selectedRig, startDate, endDate);
      return fetchAnalyticsData(
        signal,
        { selectedRig: selectedRig, startDate: start, endDate: end },
        API_ROLLUP.MIN
      ).then((resp: AnalyticsV2RespType) => {
        const rigData = resp?.data?.find(item => item.rigId === selectedRig.rigId);
        const analytics = rigData?.analytics || [];
        const res: { [key: string]: number } = {}; // This will hold the result with dynamic keys

        // Initialize `res` with default values for each warning type and unit
        Object.values(WARNINGS_TYPE).forEach((val) => {
          const warningType = analyticsFields[val as keyof typeof WARNINGS_TYPE];
          if (!warningType) return; // Skip if no matching warning type

          const { fieldName } = warningType;

          // For each unit, initialize a corresponding key in `res`
          Object.values(DISPLAY_UNIT).forEach((unit) => {
            res[`${fieldName}${unit}Waste`] = 0;
          });
        });

        // Process the response data to update `res`
        analytics.forEach((item) => {
          const warningType = analyticsFields[item.type as keyof typeof WARNINGS_TYPE];
          if (!warningType) return;

          const { fieldName } = warningType;

          Object.values(DISPLAY_UNIT).forEach((unit: string) => {
            let value = (item[`${unit.toLowerCase()}Saving`] as number) ?? 0;
            if (unit === DISPLAY_UNIT.ENERGY) {
              value /= 1000;
            }
            res[`${fieldName}${unit}Waste`] += value; // Add the value or default to 0
          });
        });

        const { duration } = periodDetails;
        const daily = Object.keys(res).reduce((acc, key) => {
          acc[key] = getDailyAvg(res[key], duration); // Apply transformation
          return acc;
        }, {} as Record<string, number | null>);

        const total = Object.keys(res).reduce((acc, key) => {
          acc[key] = res[key]; // Apply transformation
          return acc;
        }, {} as Record<string, number | null>);

        return { total: total, daily: daily };
      });
    }
  });

  useEffect(() => {
    if (!defaultOverviewDataQuery.data || !defaultAnalyticsDataQuery.data) return;

    const { daily = {}, total = {} } = defaultOverviewDataQuery.data ?? {};
    const { daily: fuelDaily = {}, total: fuelTotal = {} } = defaultAnalyticsDataQuery.data ?? {};

    let defaults = { ...total, ...fuelTotal };
    if (baseline.daily) {
      defaults = { ...daily, ...fuelDaily };
    }

    // Corrected prettyDefaults logic
    const prettyDefaults: Record<string, string | number> = {};
    Object.keys(defaults).forEach((key) => {
      prettyDefaults[key] = roundToSixDecimals(defaults[key]);
    });
    setPercentChange(0);
    setBaseline((prev) => ({
      ...prev,
      defaultTargetValues: defaults,
      percentChange: 0,
      useTargetDefaults: Object.keys(defaults),
      ...prettyDefaults // Ensure you use the processed values
    }));
  }, [baseline.daily, defaultOverviewDataQuery.data, defaultAnalyticsDataQuery.data]);

  const getHelperText = useCallback(
    (key: keyof BaselineType) => {
      let value = baseline?.[key] as number;

      const { duration, startDate, endDate } = periodDetails;
      if (!startDate || !endDate) {
        return '';
      }
      if (value) {
        if (baseline.daily) {
          value *= duration;
        } else {
          value /= duration;
        }
      }
      value = isNaN(value) ? null : parseNumberToSignificant(value);
      const returnValue: string = value ? formatNumberReadable(value) : '-';

      return baseline.daily ? `Total: ${returnValue}` : `Per day: ${returnValue}`;
    },
    [baseline, periodDetails, defaultOverviewDataQuery.data, defaultAnalyticsDataQuery.data]
  );

  const handleAutoFill = useCallback(
    (baseline: BaselineType, field: keyof BaselineType | null, percent: number | null = null) => {
      if (!baseline.defaultTargetValues) return baseline;

      const defaultValues = baseline.defaultTargetValues; // Extract here
      let percentageChange = percent;

      if (percentageChange === null && field !== null) {
        const defValue = defaultValues[field] as number;
        const currValue = baseline[field] as number;

        if (defValue !== 0) {
          percentageChange = (Math.abs(currValue - defValue) / defValue) * 100;
        } else {
          percentageChange = 0; // Avoid division by zero
        }
      }

      if (percentageChange === null) return baseline; // Ensure we have a valid percentage

      // Update baseline values for target fields, except the current field
      const updatedBaseline = { ...baseline, percentChange: percentageChange }; // DO THIS
      setPercentChange(percentageChange);

      Object.keys(defaultValues).forEach((fieldName) => {
        const key = fieldName as keyof BaselineType; // Explicitly assert type
        const originalValue = defaultValues[key] as number;
        if (field === fieldName) return;

        if (originalValue !== undefined && originalValue !== null) {
          updatedBaseline[key] = roundToSixDecimals(
            originalValue + (originalValue * (percentageChange ?? 0)) / 100
          ) as never;
        }
      });

      return updatedBaseline;
    },
    []
  );

  const handleInputChange = (val: string | null, key: keyof BaselineType) => {
    const numericRegex = /^-?\d*(\.\d*)?$/;

    if (val === null || numericRegex.test(val)) {
      let temp: BaselineType = { ...baseline };
      if (val === null) {
        temp[key] = null as never; // Type assertion to ensure key is valid
      } else {
        temp[key] = (val === '' ? '' : parseFloat(val)) as never; // Type assertion
      }
      if (baseline.autoUpdate) {
        temp = handleAutoFill(temp, key);
      }

      temp.useTargetDefaults = [...(temp.useTargetDefaults ?? [])].filter((val) => val !== key);

      setBaseline(temp);
    }
  };

  const isLoading = useMemo(() => {
    return defaultOverviewDataQuery.isFetching || defaultAnalyticsDataQuery.isFetching;
  }, [defaultOverviewDataQuery.isFetching, defaultAnalyticsDataQuery.isFetching]);

  const enablePercentInput = useMemo(() => {
    return !isLoading && !!baseline.selectedRig && !!baseline.startDate && !!baseline.endDate;
  }, [isLoading, baseline]);
  return (
    <Stack direction={'row'} sx={{ width: '100%' }} spacing={4}>
      <Stack spacing={1} sx={{ width: '100%' }}>
        <TextWithTooltipIcon
          label={
            <Typography variant={'subtitle1'} bold>
              Auto fill by rig and period
            </Typography>
          }
          tooltipText={
            'Set the target details based on data from a rig, given a specific time period.'
          }
          labelVariant={'subtitle1'}
        />

        <Stack spacing={3}>
          <RigSelect
            selectedRig={baseline.selectedRig ?? null}
            onChange={(rig: SelectedRigType) =>
              setBaseline((prev) => ({ ...prev, ...{ selectedRig: rig } }))
            }
          />
          <DateRangeDropdown
            endDate={baseline?.endDate ?? null}
            startDate={baseline?.startDate ?? null}
            selectedDateName=""
            onSave={(val: BaselineType) => setBaseline((prev) => ({ ...prev, ...val }))}
            fullWidth
            well={baseline?.well ?? null}
            allowTimeZoneChange={false}
            currentRig={baseline.selectedRig ?? null}
          />
        </Stack>
        <Stack spacing={1}>
          <Stack spacing={1} direction={'row'} alignItems={'center'}>
            <TextWithTooltipIcon
              label={
                <Typography variant={'subtitle1'} bold>
                  Auto update fields
                </Typography>
              }
              tooltipText={
                'When enabled, changing a field will automatically apply the same percentage change to all other fields using the default values from the rig and period. This will apply on both manual input changes, and update all by percentage'
              }
              labelVariant={'subtitle1'}
            />
            <Switch
              disabled={!enablePercentInput}
              size={'small'}
              checked={enablePercentInput && baseline.autoUpdate}
              onClick={() => {
                let updatedBaseline = { ...baseline };
                if (!updatedBaseline.autoUpdate) {
                  updatedBaseline = handleAutoFill(baseline, null, percentChange);
                }
                setBaseline({ ...updatedBaseline, autoUpdate: !updatedBaseline.autoUpdate });
              }}
            />
          </Stack>
          <Collapse in={enablePercentInput && baseline.autoUpdate}>
            <PercentInput
              value={percentChange}
              onChange={(value) => {
                setBaseline(handleAutoFill(baseline, null, value));
              }}
              disabled={!enablePercentInput}
            />
          </Collapse>
        </Stack>
      </Stack>
      <Stack spacing={1} sx={{ width: '100%' }}>
        <TextInput
          helperText={highlightErrors && !baseline.baselineName ? 'Please fill this field.' : ''}
          error={highlightErrors && !baseline.baselineName}
          placeholder={'Create a baseline name'}
          label={'Name'}
          value={baseline.baselineName ?? ''}
          onChange={(value) => setBaseline((prev) => ({ ...prev, ...{ baselineName: value } }))}
        />

        <Typography variant={'subtitle1'} bold>
          Overview
        </Typography>
        <Stack sx={{ pl: 5 }} spacing={periodDetails.startDate && periodDetails.endDate ? 0 : 2}>
          {overviewFields.map(({ unit, title, fieldName }) => (
            <TargetDetailsInput
              key={`${fieldName}-input`}
              loading={isLoading}
              title={title}
              unit={baseline.daily ? `${unit}/day` : unit}
              value={baseline?.[fieldName] ?? null}
              onChange={(value: string | null) => handleInputChange(value, fieldName)}
              helperText={getHelperText(fieldName)}
            />
          ))}
        </Stack>
        <Typography variant={'subtitle1'} bold>
          Analytics
        </Typography>
        {Object.values(analyticsFields)?.map(({ title, fieldName }) => (
          <TargetDetailsAnalyticsInput
            periodDetails={periodDetails}
            key={`analytics-${fieldName}`}
            title={title}
            fieldName={fieldName}
            getHelperText={getHelperText}
            isLoading={isLoading}
            handleInputChange={handleInputChange}
            baseline={baseline}
          />
        ))}
      </Stack>
    </Stack>
  );
}

TargetDetails.propTypes = {
  baseline: PropTypes.object.isRequired,
  setBaseline: PropTypes.func.isRequired,
  highlightErrors: PropTypes.bool
};

TargetDetails.defaultProps = {
  highlightErrors: false
};

export default TargetDetails;
